import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { StampMemberDirectoryRow } from '../../../../../core/models/parking/stamp-discount.model';
import { MapperStampDiscountService } from '../../../../../core/mappers/parking/mapper-stamp-discount.service';
import { ApiStampDiscountService } from '../../../../../core/services/api/parking/api-stamp-discount.service';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';

@Component({
  selector: 'app-stamp-members',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent],
  templateUrl: './stamp-members.component.html',
  styleUrl: './stamp-members.component.css',
})
export class StampMembersComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiStampDiscountService);
  private readonly mapper = inject(MapperStampDiscountService);
  private readonly toast = inject(ToastService);

  items = signal<StampMemberDirectoryRow[]>([]);
  isLoading = signal(false);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(1);
  lastFetchedTime: Date | null = null;

  constructor() {
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id;
      untracked(() => {
        this.currentPage.set(1);
        if (siteId) this.loadTable();
        else this.items.set([]);
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get startItemIndex(): number {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage() * this.pageSize(), this.totalItems());
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  ngOnInit(): void {
    if (this.selectedSiteId) this.loadTable();
  }

  onSearchChange(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  trackById(_index: number, item: StampMemberDirectoryRow): number {
    return item.memberId;
  }

  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.items.set([]);
      return;
    }

    this.isLoading.set(true);
    this.api.getStampMembersForTable({
      page: this.currentPage(),
      pageSize: this.pageSize(),
      searchText: this.searchText().trim() || undefined,
      siteId,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapMemberDirectoryTable(res);
        this.items.set(mapped.data);
        this.totalItems.set(mapped.total);
        this.totalPages.set(Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลล้มเหลว');
      },
    });
  }
}
